import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { utils } from '../../utils';
import LoadingSpinner from '../Common/LoadingSpinner';
import ModalPortal from '../Common/ModalPortal';

interface Contato {
  id: number;
  empresa_id: number;
  connection_id: number;
  jid: string;
  lid: string | null;
  telefone: string | null;
  mensagem_original: string | null;
  status: 'pendente' | 'vinculado' | 'ignorado';
  motorista_vinculado_id: string | null;
  motorista_nome: string | null;
  motorista_celular: string | null;
  vinculado_por_nome: string | null;
  vinculado_em: string | null;
  primeira_tentativa: string;
  ultima_tentativa: string;
  tentativas: number;
}

interface MotoristaDisponivel {
  id: string;
  nome: string;
  celular: string;
  cpf: string;
  lid: string | null;
}

interface Estatisticas {
  pendentes: number;
  vinculados: number;
  ignorados: number;
  total: number;
}

const ContatosNaoIdentificados: React.FC = () => {
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Estatisticas>({ pendentes: 0, vinculados: 0, ignorados: 0, total: 0 });
  const [filtroStatus, setFiltroStatus] = useState<string>('pendente');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal vincular
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState<Contato | null>(null);
  const [motoristas, setMotoristas] = useState<MotoristaDisponivel[]>([]);
  const [buscaMotorista, setBuscaMotorista] = useState('');
  const [motoristaSelecionado, setMotoristaSelecionado] = useState<string | null>(null);
  const [vinculando, setVinculando] = useState(false);
  const [loadingMotoristas, setLoadingMotoristas] = useState(false);

  const carregarContatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroStatus) params.append('status', filtroStatus);
      params.append('page', page.toString());
      params.append('limit', '15');

      const response: any = await apiService.getCustom(
        `/whatsapp/contatos-nao-identificados?${params.toString()}`
      );
      if (response.success) {
        setContatos(response.contatos || []);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, page]);

  const carregarEstatisticas = useCallback(async () => {
    try {
      const response: any = await apiService.getCustom(
        '/whatsapp/contatos-nao-identificados/estatisticas'
      );
      if (response.success) {
        setStats({
          pendentes: parseInt(response.pendentes) || 0,
          vinculados: parseInt(response.vinculados) || 0,
          ignorados: parseInt(response.ignorados) || 0,
          total: parseInt(response.total) || 0,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  useEffect(() => {
    carregarContatos();
    carregarEstatisticas();
  }, [carregarContatos, carregarEstatisticas]);

  const carregarMotoristas = async (busca: string = '') => {
    setLoadingMotoristas(true);
    try {
      const params = busca ? `?busca=${encodeURIComponent(busca)}` : '';
      const response: any = await apiService.getCustom(
        `/whatsapp/contatos-nao-identificados/motoristas-disponiveis${params}`
      );
      if (response.success) {
        setMotoristas(response.motoristas || []);
      }
    } catch (error) {
      console.error('Erro ao carregar motoristas:', error);
    } finally {
      setLoadingMotoristas(false);
    }
  };

  const abrirModalVincular = (contato: Contato) => {
    setContatoSelecionado(contato);
    setMotoristaSelecionado(null);
    setBuscaMotorista('');
    setShowVincularModal(true);
    carregarMotoristas();
  };

  const handleVincular = async () => {
    if (!contatoSelecionado || !motoristaSelecionado) return;
    setVinculando(true);
    try {
      const response: any = await apiService.postCustom(
        `/whatsapp/contatos-nao-identificados/${contatoSelecionado.id}/vincular`,
        { motorista_id: motoristaSelecionado }
      );
      if (response.success) {
        utils.showNotification('Contato vinculado ao motorista com sucesso!', { type: 'success' });
        setShowVincularModal(false);
        carregarContatos();
        carregarEstatisticas();
      }
    } catch (error) {
      console.error('Erro ao vincular:', error);
      utils.showNotification('Erro ao vincular contato', { type: 'error' });
    } finally {
      setVinculando(false);
    }
  };

  const handleIgnorar = async (contato: Contato) => {
    if (!window.confirm(`Ignorar contato ${contato.telefone || contato.jid}? Ele não aparecerá mais na lista de pendentes.`)) return;
    try {
      const response: any = await apiService.postCustom(
        `/whatsapp/contatos-nao-identificados/${contato.id}/ignorar`,
        {}
      );
      if (response.success) {
        utils.showNotification('Contato marcado como ignorado', { type: 'info' });
        carregarContatos();
        carregarEstatisticas();
      }
    } catch (error) {
      console.error('Erro ao ignorar:', error);
      utils.showNotification('Erro ao ignorar contato', { type: 'error' });
    }
  };

  const formatarTelefone = (tel: string | null) => {
    if (!tel) return '-';
    if (tel.length === 13 && tel.startsWith('55')) {
      return `+${tel.slice(0, 2)} (${tel.slice(2, 4)}) ${tel.slice(4, 9)}-${tel.slice(9)}`;
    }
    if (tel.length === 11) {
      return `(${tel.slice(0, 2)}) ${tel.slice(2, 7)}-${tel.slice(7)}`;
    }
    return tel;
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  useEffect(() => {
    if (!showVincularModal) return;
    const timeout = setTimeout(() => {
      carregarMotoristas(buscaMotorista);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaMotorista]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>
            <i className="fas fa-user-slash" style={{ marginRight: 10, color: 'var(--danger-color)' }}></i>
            Contatos Não Identificados
          </h1>
          <p>Contatos que tentaram usar o chatbot mas não foram reconhecidos como motoristas cadastrados</p>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="stat-card" style={{ background: 'var(--card-bg)', borderRadius: 8, padding: 16, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--warning-color)' }}>{stats.pendentes}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Pendentes</div>
        </div>
        <div className="stat-card" style={{ background: 'var(--card-bg)', borderRadius: 8, padding: 16, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success-color)' }}>{stats.vinculados}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Vinculados</div>
        </div>
        <div className="stat-card" style={{ background: 'var(--card-bg)', borderRadius: 8, padding: 16, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-secondary)' }}>{stats.ignorados}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ignorados</div>
        </div>
        <div className="stat-card" style={{ background: 'var(--card-bg)', borderRadius: 8, padding: 16, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary-color)' }}>{stats.total}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Total</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        {['pendente', 'vinculado', 'ignorado', ''].map((s) => (
          <button
            key={s || 'todos'}
            className={`btn btn-sm ${filtroStatus === s ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => { setFiltroStatus(s); setPage(1); }}
          >
            {s === '' ? 'Todos' : s === 'pendente' ? 'Pendentes' : s === 'vinculado' ? 'Vinculados' : 'Ignorados'}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="page-content">
        {loading ? (
          <LoadingSpinner />
        ) : contatos.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: 40 }}>
            <i className="fas fa-check-circle" style={{ fontSize: 48, color: 'var(--success-color)', marginBottom: 16 }}></i>
            <h3>Nenhum contato {filtroStatus === 'pendente' ? 'pendente' : filtroStatus || ''}</h3>
            <p>Todos os contatos que tentaram usar o chatbot foram identificados corretamente.</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Telefone / JID</th>
                    <th>LID</th>
                    <th>Tentativas</th>
                    <th>Primeira Tentativa</th>
                    <th>Última Tentativa</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {contatos.map((contato) => (
                    <tr key={contato.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {contato.telefone ? formatarTelefone(contato.telefone) : '-'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {contato.jid}
                        </div>
                      </td>
                      <td>
                        <code style={{ fontSize: 11 }}>{contato.lid || '-'}</code>
                      </td>
                      <td>
                        <span className={`badge ${contato.tentativas > 3 ? 'badge-danger' : 'badge-warning'}`}>
                          {contato.tentativas}x
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{formatarData(contato.primeira_tentativa)}</td>
                      <td style={{ fontSize: 12 }}>{formatarData(contato.ultima_tentativa)}</td>
                      <td>
                        {contato.status === 'pendente' && (
                          <span className="badge badge-warning">Pendente</span>
                        )}
                        {contato.status === 'vinculado' && (
                          <span className="badge badge-success" title={`Vinculado a: ${contato.motorista_nome || '-'}`}>
                            Vinculado
                          </span>
                        )}
                        {contato.status === 'ignorado' && (
                          <span className="badge badge-secondary">Ignorado</span>
                        )}
                        {contato.motorista_nome && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                            <i className="fas fa-user" style={{ marginRight: 4 }}></i>
                            {contato.motorista_nome}
                          </div>
                        )}
                      </td>
                      <td>
                        {contato.status === 'pendente' && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => abrirModalVincular(contato)}
                              title="Vincular a motorista"
                            >
                              <i className="fas fa-link"></i>
                              Vincular
                            </button>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => handleIgnorar(contato)}
                              title="Ignorar contato"
                            >
                              <i className="fas fa-ban"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </button>
                <span style={{ padding: '6px 12px', fontSize: 13 }}>
                  Página {page} de {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Vincular Motorista */}
      <ModalPortal isOpen={showVincularModal}>
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3>Vincular a Motorista</h3>
              <button className="btn-close" onClick={() => setShowVincularModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {contatoSelecionado && (
                <div style={{
                  background: 'var(--background-secondary, #f5f5f5)',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 16,
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Contato não identificado:</div>
                  <div style={{ fontSize: 13 }}>
                    <div><strong>Telefone:</strong> {formatarTelefone(contatoSelecionado.telefone)}</div>
                    <div><strong>JID:</strong> {contatoSelecionado.jid}</div>
                    {contatoSelecionado.lid && <div><strong>LID:</strong> {contatoSelecionado.lid}</div>}
                    <div><strong>Tentativas:</strong> {contatoSelecionado.tentativas}</div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Buscar motorista:</label>
                <input
                  type="text"
                  value={buscaMotorista}
                  onChange={(e) => setBuscaMotorista(e.target.value)}
                  placeholder="Nome, celular ou CPF..."
                  autoFocus
                />
              </div>

              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                {loadingMotoristas ? (
                  <div style={{ padding: 20, textAlign: 'center' }}><LoadingSpinner /></div>
                ) : motoristas.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Nenhum motorista encontrado
                  </div>
                ) : (
                  motoristas.map((m) => (
                    <div
                      key={m.id}
                      onClick={() => setMotoristaSelecionado(m.id)}
                      style={{
                        padding: '10px 14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--border-color)',
                        background: motoristaSelecionado === m.id ? 'var(--primary-color-light, #e3f2fd)' : 'transparent',
                        transition: 'background 0.2s',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {motoristaSelecionado === m.id && (
                          <i className="fas fa-check-circle" style={{ color: 'var(--primary-color)', marginRight: 6 }}></i>
                        )}
                        {m.nome}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {m.celular ? formatarTelefone(m.celular) : 'Sem celular'}
                        {m.cpf && ` | CPF: ${m.cpf}`}
                        {m.lid && ` | LID: ${m.lid}`}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowVincularModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                disabled={!motoristaSelecionado || vinculando}
                onClick={handleVincular}
              >
                {vinculando ? (
                  <><i className="fas fa-spinner fa-spin"></i> Vinculando...</>
                ) : (
                  <><i className="fas fa-link"></i> Vincular Motorista</>
                )}
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    </div>
  );
};

export default ContatosNaoIdentificados;
